import { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import {
  MdAttachMoney,
  MdEventBusy,
  MdFitnessCenter,
  MdSubscriptions,
  MdTrendingUp,
} from 'react-icons/md';
import PageHeader from '../../components/owner/PageHeader';
import PanelStatCard from '../../components/shared/PanelStatCard';
import { fetchDashboardData } from '../../services/owner/financeApi';
import { peekOwnerCache } from '../../services/cache/ownerCache';
import { usePlatformSettings } from '../../context/PlatformSettingsContext';
import './ownerPages.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Filler,
);

const CHART_REVENUE = '#f59e0b';
const CHART_SUBSCRIPTIONS = '#3b82f6';
const CHART_SIGNUPS = '#94FF00';
const CHART_CHURN = '#ef4444';
const TEXT_SECONDARY = '#94a3b8';
const GRID_COLOR = 'rgba(148, 163, 184, 0.08)';

const statIcons = {
  gyms: MdFitnessCenter,
  subscriptions: MdSubscriptions,
  expired: MdEventBusy,
  revenue: MdAttachMoney,
  'annual-revenue': MdTrendingUp,
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#1A1F2B',
      borderColor: 'rgba(148, 163, 184, 0.14)',
      borderWidth: 1,
      titleColor: '#f1f5f9',
      bodyColor: TEXT_SECONDARY,
    },
  },
  scales: {
    x: {
      ticks: { color: TEXT_SECONDARY },
      grid: { color: GRID_COLOR },
    },
    y: {
      ticks: { color: TEXT_SECONDARY },
      grid: { color: GRID_COLOR },
    },
  },
};

function Dashboard() {
  const { formatPrice, formatCompact } = usePlatformSettings();
  const cached = peekOwnerCache('dashboard');
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(cached?.stats || []);
  const [revenueChartData, setRevenueChartData] = useState(cached?.revenueChartData || { labels: [], values: [] });
  const [subscriptionGrowthData, setSubscriptionGrowthData] = useState(cached?.subscriptionGrowthData || { labels: [], values: [] });
  const [newSignupsData, setNewSignupsData] = useState(cached?.newSignupsData || { labels: [], values: [] });
  const [churnData, setChurnData] = useState(cached?.churnData || { labels: [], values: [] });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!peekOwnerCache('dashboard')) setLoading(true);
        setError('');
        const data = await fetchDashboardData();
        if (cancelled) return;
        setStats(data.stats);
        setRevenueChartData(data.revenueChartData);
        setSubscriptionGrowthData(data.subscriptionGrowthData);
        setNewSignupsData(data.newSignupsData);
        setChurnData(data.churnData);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const revenueChartOptions = {
    ...chartOptions,
    scales: {
      ...chartOptions.scales,
      y: {
        ...chartOptions.scales.y,
        ticks: {
          color: TEXT_SECONDARY,
          callback: (value) => formatCompact(value),
        },
      },
    },
  };

  const revenueDataset = {
    labels: revenueChartData.labels,
    datasets: [{
      label: 'Revenue',
      data: revenueChartData.values,
      backgroundColor: 'rgba(245, 158, 11, 0.75)',
      borderColor: CHART_REVENUE,
      borderWidth: 1,
      borderRadius: 6,
    }],
  };

  const subscriptionDataset = {
    labels: subscriptionGrowthData.labels,
    datasets: [{
      label: 'Active Subscriptions',
      data: subscriptionGrowthData.values,
      borderColor: CHART_SUBSCRIPTIONS,
      backgroundColor: 'rgba(59, 130, 246, 0.14)',
      fill: true,
      tension: 0.35,
      pointBackgroundColor: CHART_SUBSCRIPTIONS,
      pointBorderColor: '#0F1218',
      pointBorderWidth: 2,
      pointRadius: 4,
    }],
  };

  const signupsDataset = {
    labels: newSignupsData.labels,
    datasets: [{
      label: 'New Gyms',
      data: newSignupsData.values,
      backgroundColor: 'rgba(148, 255, 0, 0.7)',
      borderColor: CHART_SIGNUPS,
      borderWidth: 1,
      borderRadius: 6,
    }],
  };

  const churnDataset = {
    labels: churnData.labels,
    datasets: [{
      label: 'Expired Subscriptions',
      data: churnData.values,
      borderColor: CHART_CHURN,
      backgroundColor: 'rgba(239, 68, 68, 0.14)',
      fill: true,
      tension: 0.35,
      pointBackgroundColor: CHART_CHURN,
      pointBorderColor: '#0F1218',
      pointBorderWidth: 2,
      pointRadius: 4,
    }],
  };

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Overview of your gym management platform" />
      {loading && <p className="owner-placeholder-text owner-mb-16">Loading dashboard...</p>}
      {error && <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>{error}</p>}

      <div className="rec-panel-stat-grid">
        {stats.map((stat) => {
          const Icon = statIcons[stat.key];
          return (
            <PanelStatCard
              key={stat.key}
              statKey={stat.key}
              label={stat.label}
              value={stat.isMoney ? formatPrice(stat.value) : stat.value}
              variant={stat.variant}
              icon={Icon}
            />
          );
        })}
      </div>

      <div className="owner-grid-2 owner-mt-24">
        <div className="owner-card rec-panel-card owner-chart-card">
          <h2 className="owner-card-title">Revenue Overview</h2>
          <div className="owner-chart-container">
            <Bar data={revenueDataset} options={revenueChartOptions} />
          </div>
        </div>
        <div className="owner-card rec-panel-card owner-chart-card">
          <h2 className="owner-card-title">Subscription Growth</h2>
          <div className="owner-chart-container">
            <Line data={subscriptionDataset} options={chartOptions} />
          </div>
        </div>
        <div className="owner-card rec-panel-card owner-chart-card">
          <h2 className="owner-card-title">New Gym Signups</h2>
          <div className="owner-chart-container">
            <Bar data={signupsDataset} options={chartOptions} />
          </div>
        </div>
        <div className="owner-card rec-panel-card owner-chart-card">
          <h2 className="owner-card-title">Subscription Churn</h2>
          <div className="owner-chart-container">
            <Line data={churnDataset} options={chartOptions} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
